import {gql} from '@apollo/client';
import {compact, objectFromEntries} from '@benchling/js-utils';
import {Color, Graphic} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useMemo, useState} from 'react';

import {AnalysisTemplateGraphQL} from 'analysis-tool/templates/AnalysisTemplateTypes';
import {AnalysisTemplateFragment} from 'analysis-tool/templates/graphql/GetAnalysisTemplate';
import isArchived from 'archive/isArchived';
import BenchlingConfig from 'BenchlingConfig';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {SectionWrapper} from 'metadata/MetadataEditorInfoSection';
import {AnalysisTemplateConfigModalContents} from 'procedures/design/analysis-template-config/AnalysisTemplateConfigModal';
import {parseAndValidateAnalysisTemplateConfigData} from 'procedures/design/analysis-template-config/AnalysisTemplateConfigTypes';
import {useAnalysisSourcesDuringExecution} from 'procedures/design/analysis-template-config/useAnalysisSources';
import {
  AnalysisSourcesOrigin,
  WorksheetAnalysisSources,
} from 'procedures/design/ProcedureMethodAnalysisTypes';
import {ProcedureAnalysisTemplateConfiguration} from 'procedures/ProcedureDomainTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {WorksheetAnalyzeButtonProcedureMethodQuery} from 'procedures/shared-components/analysis/__generated/WorksheetAnalyzeButtonProcedureMethodQuery';
import AnalysisTemplateConfigModalContext, {
  AnalysisTemplateConfigModalContextProperties,
  useAnalysisTemplateConfigModalContext,
} from 'procedures/shared-components/analysis/AnalysisTemplateConfigModalContext';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {CenteredSpinner} from 'ui-library/Spinner';

import {AnalysisTemplatesQuery} from './__generated/AnalysisTemplatesQuery';
import {getTableMappingsWithUpdatedLabAutoDatasetIds} from './preconfiguredAnalysisUtils';

export interface ConfiguredAnalysisTemplate extends ProcedureAnalysisTemplateConfiguration {
  name: string;
}

export type AccessiblePMEI = ExcludeInaccessible<
  WorksheetAnalyzeButtonProcedureMethodQuery['procedureMethodExecutionInstance']
>;

type AccessiblePlanMethodDefinitionVersion = ExcludeInaccessible<
  AccessiblePMEI['planMethodDefinitionVersion']
>;

type AnalysisTemplateConfig = AccessiblePlanMethodDefinitionVersion['analysisTemplateConfigurations'][number];

type AccessibleAnalysisTemplateConfig = ExcludeInaccessible<AnalysisTemplateConfig>;

function isAnalysisTemplateConfigAccessible(
  analysisTemplateConfig: AnalysisTemplateConfig
): analysisTemplateConfig is AccessibleAnalysisTemplateConfig {
  return !isInaccessible(analysisTemplateConfig);
}

export const ANALYSIS_TEMPLATES_QUERY = gql`
  query AnalysisTemplatesQuery($ids: [StrictString!]!) @skipAudit @engComponent(name: PROCEDURES) {
    analysisTemplates(ids: $ids) {
      ... on AnalysisTemplateNode {
        ...AnalysisTemplateFragment
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${AnalysisTemplateFragment.fragment}
`;

export interface AnalyzePreconfiguredProcedureState {
  analysisTemplateConfig: {
    hasConfiguredTemplates: boolean;
    availablePlateIdsToSummarize: Array<string>;
    configModalContext: AnalysisTemplateConfigModalContextProperties;
  };
  selectedAnalysisTemplateInfo: ConfiguredAnalysisTemplate | null;
  analyzePreconfiguredProcedurePane: JSX.Element | null;
}

export function useAnalyzePreconfiguredProcedureState(
  pmei: AccessiblePMEI | null,
  worksheetId: string
): AnalyzePreconfiguredProcedureState {
  const configuredTemplates: Array<ConfiguredAnalysisTemplate> =
    pmei != null && !isInaccessible(pmei.planMethodDefinitionVersion)
      ? compact(
          pmei.planMethodDefinitionVersion.analysisTemplateConfigurations.map(
            (config): null | ConfiguredAnalysisTemplate =>
              isAnalysisTemplateConfigAccessible(config) && !isArchived(config.analysisTemplate)
                ? {
                    analysisTemplateId: config.analysisTemplate.id,
                    name: config.analysisTemplate.name,
                    // This code will convert the JSON into a valid, properly typed config object. If any of the
                    // configs are invalid, it will throw
                    config: parseAndValidateAnalysisTemplateConfigData(config.configJson),
                  }
                : null
          )
        )
      : [];
  const [selectedAnalysisTemplateInfo, setSelectedAnalysisTemplateInfo] = useState(
    configuredTemplates.length === 1 ? configuredTemplates[0] : null
  );
  const {analysisSources} = useAnalysisSourcesDuringExecution(worksheetId);

  // For lab auto datasets, we use output file config API IDs as the datasetIds in the table mappings during configuration.
  // Since the datasets have been generated when we get to execution, we refer to the actual dataset API IDs here (in both the selected sources and the seed data config).
  // Therefore, we need to update the table mappings in the analysis template config to refer to the dataset API IDs.
  useEffect(() => {
    if (analysisSources != null) {
      setSelectedAnalysisTemplateInfo(
        (selectedAnalysisTemplateInfo) =>
          selectedAnalysisTemplateInfo && {
            ...selectedAnalysisTemplateInfo,
            config: {
              ...selectedAnalysisTemplateInfo.config,
              tableMappings: getTableMappingsWithUpdatedLabAutoDatasetIds(
                selectedAnalysisTemplateInfo.config.tableMappings,
                analysisSources
              ),
            },
          }
      );
    }
    // Update the table mappings when a different analysis template is selected
  }, [analysisSources, selectedAnalysisTemplateInfo?.analysisTemplateId]);

  const availablePlateIdsToSummarize =
    analysisSources != null
      ? analysisSources.sourcesPerStep.flatMap((stepSources) =>
          stepSources.analysisInputs.plates.map((plate) => plate.plateId)
        )
      : [];

  const configModalContext = useAnalysisTemplateConfigModalContext(selectedAnalysisTemplateInfo);
  return {
    analysisTemplateConfig: {
      hasConfiguredTemplates: configuredTemplates.length > 0,
      availablePlateIdsToSummarize,
      configModalContext,
    },
    selectedAnalysisTemplateInfo,
    analyzePreconfiguredProcedurePane:
      pmei != null ? (
        <AnalysisTemplateConfigModalContext.Provider value={configModalContext}>
          <AnalyzePreconfiguredProcedureModalContents
            worksheetId={worksheetId}
            analysisSources={analysisSources}
            configuredTemplates={configuredTemplates}
            selectedAnalysisTemplateInfo={selectedAnalysisTemplateInfo}
            setSelectedAnalysisTemplateInfo={setSelectedAnalysisTemplateInfo}
            procedureType={pmei.procedureRun.procedureType}
          />
        </AnalysisTemplateConfigModalContext.Provider>
      ) : null,
  };
}

interface AnalyzePreconfiguredProcedureModalContentsProps {
  worksheetId: string;
  // null indicates that we haven't received the API response yet
  analysisSources: WorksheetAnalysisSources | null;
  procedureType: ProcedureType;
  configuredTemplates: Array<ConfiguredAnalysisTemplate>;
  selectedAnalysisTemplateInfo: ConfiguredAnalysisTemplate | null;
  setSelectedAnalysisTemplateInfo: (newTemplateInfo: ConfiguredAnalysisTemplate) => void;
}

/** @package Exported for testing */
export interface PreconfiguredAnalysisTemplateSelectorProps {
  selectedTemplateName: string | null | undefined;
  configuredTemplates: Array<ConfiguredAnalysisTemplate>;
  onSelect: (selectedTemplate: string) => void;
  disabled?: boolean;
}

/** @package Exported for testing */
export function PreconfiguredAnalysisTemplateSelector(
  props: PreconfiguredAnalysisTemplateSelectorProps
): JSX.Element | null {
  return (
    <SelectDropdown
      displayName={props.selectedTemplateName ?? 'Select automation template'}
      disabled={props.disabled}
      menu={
        <Menu width={Menu.WIDTHS.FULL}>
          {props.configuredTemplates.map(({analysisTemplateId, name}) => (
            <Option
              key={analysisTemplateId}
              onSelect={() => {
                props.onSelect(analysisTemplateId);
              }}
            >
              {name}
            </Option>
          ))}
        </Menu>
      }
    />
  );
}

/** @package Exported for testing */
export function AnalyzePreconfiguredProcedureModalContents(
  props: AnalyzePreconfiguredProcedureModalContentsProps
): JSX.Element {
  const queryResult = useBenchlingQuery<AnalysisTemplatesQuery>(ANALYSIS_TEMPLATES_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      ids: props.configuredTemplates.map(({analysisTemplateId}) => analysisTemplateId),
    },
  });

  const accessibleTemplatesById: Record<string, AnalysisTemplateGraphQL> = useMemo(
    () =>
      queryResult.type !== QueryResultType.SUCCESS
        ? {}
        : objectFromEntries(
            accessibleObjs(queryResult.data.analysisTemplates).map((template) => [template.id, template])
          ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryResult.type]
  );

  const selectedAnalysisTemplate =
    props.selectedAnalysisTemplateInfo == null
      ? null
      : accessibleTemplatesById[props.selectedAnalysisTemplateInfo.analysisTemplateId];

  const configModalContext = AnalysisTemplateConfigModalContext.useContext();

  if (queryResult.type !== QueryResultType.SUCCESS) {
    return <CenteredSpinner />;
  }

  return props.configuredTemplates.length === 0 ? (
    <InlineBanner
      bannerType={InlineBannerType.WARNING}
      text={`No automation templates are configured for this ${getProcedureUIString(
        ProceduresStringKeys.METHOD,
        props.procedureType,
        {
          stringCase: StringCase.LOWER,
        }
      )}.`}
    />
  ) : (
    <div>
      <SectionWrapper headerName="Automation template">
        <div className={css(styles.templateSelector)}>
          <PreconfiguredAnalysisTemplateSelector
            disabled={props.configuredTemplates.length === 1}
            configuredTemplates={props.configuredTemplates}
            selectedTemplateName={props.selectedAnalysisTemplateInfo?.name}
            onSelect={(selectedTemplateId) => {
              const newSelectedTemplateInfo = props.configuredTemplates.find(
                ({analysisTemplateId}) => analysisTemplateId === selectedTemplateId
              )!;
              props.setSelectedAnalysisTemplateInfo(newSelectedTemplateInfo);
              configModalContext.setFullConfig(newSelectedTemplateInfo);
            }}
          />
        </div>
      </SectionWrapper>
      {BenchlingConfig.STUDIES_ENABLE_ANALYSIS_DATA_REFRESH && (
        <div className={css(styles.templateNonRefreshableInfoBanner)}>
          <InlineBanner
            bannerType={InlineBannerType.INFO}
            text="Analyses created with a template aren't refreshable"
          />
        </div>
      )}
      {selectedAnalysisTemplate == null ||
      props.analysisSources == null ||
      props.selectedAnalysisTemplateInfo == null ? (
        <div className={css(styles.graphicWithCaption)}>
          <Graphic name="flowchart" className={css(styles.graphic)} />
          <div className={css(styles.graphicCaption)}>Select an automation template above to get started</div>
        </div>
      ) : (
        <div>
          <AnalysisTemplateConfigModalContents
            // Force a new component to be built when the selected analysis changes
            key={props.selectedAnalysisTemplateInfo.analysisTemplateId}
            analysisTemplate={selectedAnalysisTemplate}
            availableSources={props.analysisSources}
            initialConfiguration={props.selectedAnalysisTemplateInfo}
            // When all "used in" values are null, the column is suppressed in the sources table
            analysisTemplateConfigContext={{usedInWhichAnalysisTemplates: () => null}}
            analysisSourcesOrigin={AnalysisSourcesOrigin.EXECUTION}
          />
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  graphic: {
    height: '150px',
    width: '150px',
    color: Color.GRAY_300,
  },
  graphicCaption: {
    color: Color.GRAY_600,
    fontSize: '13px',
  },
  graphicWithCaption: {
    alignItems: 'center',
    background: Color.GRAY_50,
    border: `1px solid ${Color.GRAY_200}`,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    paddingLeft: '10px',
    paddingRight: '10px',
    textAlign: 'center',
    height: '400px',
    width: '100%',
  },
  templateSelector: {
    marginBottom: '20px',
  },
  templateNonRefreshableInfoBanner: {
    marginBottom: '16px',
  },
});
